'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import Link from 'next/link';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { ROUTES } from '@/constants/routes';

import {
  forgotPasswordSchema,
  type ForgotPasswordFormValues,
} from '@/modules/auth/schemas/auth-forms.schema';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';

export default function ForgotPasswordPage() {
  const [submitted, setSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: '' },
  });

  const onSubmit = handleSubmit(() => {
    setSubmitted(true);
    reset();
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Reset access</CardTitle>
        <CardDescription>
          Capture the primary email linked to VunaPay. The backend reset pipeline will plug in once
          compliance signs off.
        </CardDescription>
      </CardHeader>

      {submitted ? (
        <Alert variant="success" title="Request captured locally">
          <p>
            Password resets are not yet wired to the API — your submission validates UX flows only.
            Contact operations if you urgently require assistance.
          </p>
        </Alert>
      ) : null}

      <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
        <Input
          label="Email"
          type="email"
          autoComplete="email"
          error={errors.email?.message}
          {...register('email')}
        />

        <Button type="submit" className="w-full">
          Send reset guidance
        </Button>

        <p className="text-center text-sm text-muted-foreground">
          Remembered your credentials?{' '}
          <Link className="font-semibold text-primary underline-offset-4 hover:underline" href={ROUTES.login}>
            Back to sign-in
          </Link>
        </p>
      </form>
    </Card>
  );
}
